
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { OrderConfirmationHeader } from "@/components/order/OrderConfirmationHeader";
import { OrderPaymentAmount } from "@/components/order/OrderPaymentAmount";
import { OrderTransferInfo } from "@/components/order/OrderTransferInfo";
import { OrderNextSteps } from "@/components/order/OrderNextSteps";
import { OrderSupport } from "@/components/order/OrderSupport";
import { OrderItemsList } from "@/components/order/OrderItemsList";
import { Skeleton } from "@/components/ui/skeleton";
import { sendOrderEmail } from "@/utils/email";

interface OrderData {
  id: string;
  order_number: number;
  total: number;
  subtotal: number;
  tps: number;
  tvq: number;
  created_at: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  shipping_address: string;
  email_sent: boolean;
  status: string;
  payment_status: string;
  order_items: Array<{
    product_name: string;
    variant_size: string;
    variant_color: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    product_id: string;
  }>;
}

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  // Verify payment on mount if session_id is present
  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId || !orderId || paymentVerified) return;

      console.log("Verifying Stripe payment", { sessionId, orderId });

      try {
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { sessionId, orderId },
        });

        if (error) {
          console.error("Payment verification error:", error);
          toast({
            title: "Erreur de vérification",
            description: "Impossible de vérifier le paiement. Contactez le support.",
            variant: "destructive",
          });
          return;
        }

        if (data.success && data.paymentStatus === 'paid') {
          console.log("Payment verified successfully");
          setPaymentVerified(true);

          // Clear cart from localStorage after successful payment
          localStorage.removeItem('cart-storage');
          
          // Clear pending order
          localStorage.removeItem('pendingOrder');

          // Fetch order details to send email
          const { data: orderData, error: orderError } = await (supabase as any)
            .from("orders")
            .select(`
              *,
              order_items (
                product_name,
                variant_size,
                variant_color,
                quantity,
                unit_price,
                total_price,
                product_id
              )
            `)
            .eq("id", orderId)
            .single();

          if (orderError) {
            console.error("Error fetching order details:", orderError);
            return;
          }

          // Get user profile
          const { data: profile } = await (supabase as any)
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (profile && orderData) {
            // Send confirmation email
            const cartItems = orderData.order_items.map((item: any) => ({
              id: item.product_id,
              name: item.product_name,
              price: item.unit_price,
              quantity: item.quantity,
              variant: {
                size: item.variant_size,
                colorName: item.variant_color,
              },
            }));

            await sendOrderEmail({
              customerEmail: orderData.customer_email,
              customerProfile: {
                first_name: profile.first_name,
                last_name: profile.last_name,
                phone: profile.phone,
                address: orderData.shipping_address,
                company: profile.company,
              },
              products: cartItems,
              orderDetails: {
                subtotal: orderData.subtotal,
                tps: orderData.tps,
                tvq: orderData.tvq,
                total: orderData.total,
                orderId: orderData.id,
                orderNumber: orderData.order_number,
              },
            });

            console.log("Confirmation email sent");
          }

          toast({
            title: "Paiement réussi",
            description: "Votre commande a été confirmée avec succès !",
          });
        }
      } catch (error) {
        console.error("Payment verification failed:", error);
      }
    };

    if (user) {
      verifyPayment();
    }
  }, [sessionId, orderId, user, paymentVerified, toast]);

  useEffect(() => {
    console.log("OrderConfirmation: Checking authentication", { userExists: !!user });
    
    // Set a timeout to ensure we don't get stuck in a loading state
    const authCheckTimeout = setTimeout(() => {
      setIsAuthChecked(true);
      if (!user) {
        console.log("OrderConfirmation: Auth check timeout reached, redirecting to login");
        toast({
          title: "Accès non autorisé",
          description: "Veuillez vous connecter pour accéder à cette page",
          variant: "destructive",
        });
        navigate("/account");
      }
    }, 1500);
    
    // If we already have user information, clear the timeout and set auth as checked
    if (user !== undefined) {
      clearTimeout(authCheckTimeout);
      setIsAuthChecked(true);
      
      if (!user) {
        console.log("OrderConfirmation: Unauthorized access attempt, redirecting to login");
        toast({
          title: "Accès non autorisé",
          description: "Veuillez vous connecter pour accéder à cette page",
          variant: "destructive",
        });
        navigate("/account");
      }
    }
    
    return () => clearTimeout(authCheckTimeout);
  }, [user, navigate, toast]);

  const { data: orderData, isLoading, error } = useQuery({
    queryKey: ['order', orderId || 'last', user?.id],
    queryFn: async (): Promise<OrderData | null> => {
      console.log("OrderConfirmation: Fetching order", { orderId, userId: user?.id });
      
      if (!user) {
        console.log("OrderConfirmation: No user found, skipping fetch");
        return null;
      }

      try {
        let query = (supabase as any)
          .from("orders")
          .select(`
            id,
            order_number,
            total,
            subtotal,
            tps,
            tvq,
            created_at,
            customer_name,
            customer_email,
            customer_phone,
            shipping_address,
            email_sent,
            status,
            payment_status,
            order_items (
              product_name,
              variant_size,
              variant_color,
              quantity,
              unit_price,
              total_price,
              product_id
            )
          `)
          .eq("user_id", user.id);

        // If we have an orderId from the URL, fetch that specific order
        if (orderId) {
          query = query.eq("id", orderId).single();
        } else {
          // Otherwise, fetch the most recent order
          query = query.order("created_at", { ascending: false }).limit(1).maybeSingle();
        }

        const { data, error } = await query;

        if (error) {
          console.error("OrderConfirmation: Error fetching order:", error);
          throw error;
        }

        if (!data) {
          console.log("OrderConfirmation: No order found");
          return null;
        }

        console.log("OrderConfirmation: Order data fetched successfully:", data);
        return data;
      } catch (error) {
        console.error("OrderConfirmation: Critical error during order fetch:", error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 30000,
    retry: 2,
  });

  // Show loading state if auth check or data is still loading
  if (!isAuthChecked || (user && isLoading)) {
    return (
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-3xl">
        <div className="bg-gradient-to-b from-green-50 to-white rounded-lg shadow-lg p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    console.error("OrderConfirmation: Error in React Query:", error);
    return (
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-3xl">
        <div className="bg-gradient-to-b from-red-50 to-white rounded-lg shadow-lg p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600">Une erreur est survenue</h2>
            <p className="text-gray-600 mt-2">Impossible de charger les détails de votre commande.</p>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="mt-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retourner à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show "no order found" state
  if (user && !orderData) {
    return (
      <div className="container mx-auto px-4 py-8 sm:py-12 max-w-3xl">
        <div className="bg-gradient-to-b from-gray-50 to-white rounded-lg shadow-lg p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-800">Aucune commande trouvée</h2>
            <p className="text-gray-600 mt-2">Nous n'avons pas trouvé de commande récente pour votre compte.</p>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="mt-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retourner à l'accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Authentication or order data is missing, this shouldn't normally happen due to the checks above
  if (!user || !orderData) return null;

  return (
    <div className="container mx-auto px-4 py-8 sm:py-12 max-w-3xl">
      <div className="bg-gradient-to-b from-green-50 to-white rounded-lg shadow-lg p-4 sm:p-8 space-y-6 sm:space-y-8">
        <OrderConfirmationHeader 
          orderNumber={orderData.order_number}
          customerName={orderData.customer_name}
          customerEmail={orderData.customer_email}
          shippingAddress={orderData.shipping_address}
        />
        
        <OrderItemsList items={orderData.order_items} />
        
        <div className="border-t border-gray-200 pt-6 sm:pt-8">
          <OrderPaymentAmount 
            total={orderData.total} 
            subtotal={orderData.subtotal}
            tps={orderData.tps}
            tvq={orderData.tvq}
          />
        </div>

        {orderData.payment_status === 'paid' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-green-800 mb-2">✓ Paiement confirmé</h2>
            <p className="text-green-700">
              Votre paiement a été traité avec succès. Votre commande sera bientôt expédiée.
            </p>
          </div>
        ) : (
          <>
            <OrderTransferInfo orderNumber={orderData.order_number} />
            <OrderNextSteps total={orderData.total} />
          </>
        )}
        <OrderSupport />

        <div className="pt-4 sm:pt-6">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="w-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retourner à l'accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
